/**
 * Design Tokens — Auge Design System
 * Direction: Operational Sophistication
 *
 * Surfaces silenciosas, cor só com significado, densidade alta sem claustrofobia.
 * Azul #3699cf como único acento. Cor semântica apenas para status.
 *
 * Usage: import { COLORS, SHADOWS, RADIUS, SPACING } from '~styles/tokens';
 */

// ─── Surfaces (Elevation Hierarchy) ─────────────────────────────────────────
export const SURFACES = {
  canvas: '#f5f7fa',      // Level 0: page background
  card: '#ffffff',         // Level 1: cards, panels
  float: '#ffffff',        // Level 2: dropdowns, popovers
  overlay: '#ffffff',      // Level 3: modals, dialogs
  inset: '#f8fafc',        // Recessed: table headers, hover states
  alternative: '#f0f3f5',  // Disabled bg, tab containers
} as const;

// ─── Colors ─────────────────────────────────────────────────────────────────
export const COLORS = {
  // Brand (único acento)
  primary: '#3699cf',
  primaryHover: '#2d7ab3',
  primary10: 'rgba(54, 153, 207, 0.1)',
  primary20: 'rgba(54, 153, 207, 0.2)',

  // Semantic
  success: '#22c55e',
  successLight: '#dcfce7',
  successDark: '#166534',

  warning: '#f59e0b',
  warningLight: '#fef3c7',
  warningDark: '#92400e',

  danger: '#ef4444',
  dangerLight: '#fee2e2',
  dangerDark: '#991b1b',
  dangerHover: '#dc2626',

  info: '#3b82f6',
  infoLight: '#dbeafe',
  infoDark: '#1e40af',

  purple: '#8b5cf6',
  purpleLight: '#f3e8ff',
  purpleDark: '#7c3aed',

  // Text hierarchy (4 levels)
  textPrimary: '#1a2127',
  textSecondary: '#40464b',
  textTertiary: '#666b6f',
  textMuted: '#9ca3af',

  // Border hierarchy (3 levels)
  borderDefault: '#e2e5e7',
  borderSubtle: '#f0f3f5',
  borderStrong: '#d1d5db',

  // Neutrals
  white: '#ffffff',
  neutral50: '#f8fafc',
  neutral100: '#f0f3f5',
  neutral200: '#e2e5e7',
  neutral300: '#d1d5db',
  neutral400: '#9ca3af',
  neutral500: '#666b6f',
  neutral600: '#40464b',
  neutral700: '#374151',
  neutral900: '#1a2127',
} as const;

// ─── Status Badge Variants ──────────────────────────────────────────────────
export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'purple';

export const BADGE_COLORS: Record<BadgeVariant, { bg: string; text: string }> = {
  success: { bg: COLORS.successLight, text: COLORS.successDark },
  warning: { bg: COLORS.warningLight, text: COLORS.warningDark },
  danger: { bg: COLORS.dangerLight, text: COLORS.dangerDark },
  info: { bg: COLORS.infoLight, text: COLORS.infoDark },
  neutral: { bg: '#f3f4f6', text: '#374151' },
  purple: { bg: COLORS.purpleLight, text: COLORS.purpleDark },
} as const;

// ─── Spacing ────────────────────────────────────────────────────────────────
export const SPACING = {
  xs: '4px',     // --space-1: icon gaps, tight pairs
  sm: '8px',     // --space-2: default gap, badge padding
  md: '12px',    // --space-3: button padding-y, card inner gap
  base: '16px',  // --space-4: section gaps, button padding-x
  lg: '20px',    // --space-5: card padding (compact)
  xl: '24px',    // --space-6: card padding (standard), page padding
  '2xl': '32px', // --space-8: major section separation
} as const;

// ─── Border Radius ──────────────────────────────────────────────────────────
export const RADIUS = {
  sm: '4px',     // Small tags, minimal rounding
  md: '8px',     // Buttons, inputs, dropdowns, controls
  lg: '12px',    // Cards, modals, containers
  full: '999px', // Badges, pills, status indicators
  circle: '50%', // Avatars, icon wrappers
} as const;

// ─── Shadows ────────────────────────────────────────────────────────────────
export const SHADOWS = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
  md: '0 4px 12px rgba(0, 0, 0, 0.12)',
  lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  focus: `0 0 0 3px ${COLORS.primary10}`,
} as const;

// ─── Typography ─────────────────────────────────────────────────────────────
export const FONT_SIZES = {
  xs: '11px',    // Table headers, badge labels (uppercase, weight 600)
  sm: '13px',    // Captions, secondary info
  base: '14px',  // Body, table cells, buttons
  md: '16px',    // Card titles, nav items
  lg: '18px',    // Section headings (h2)
  '2xl': '28px', // Page title (h1)
  '3xl': '32px', // KPI hero values
} as const;

export const FONT_WEIGHTS = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ─── Transitions ────────────────────────────────────────────────────────────
export const TRANSITIONS = {
  fast: '0.15s ease',
  normal: '0.2s ease',
  slow: '0.3s ease-out',
} as const;
